import { Segment } from '@mi-front/core';
import { Milliseconds, toDateTimeStr, toTimestamp } from '@mi-front/core';
import { graphPalette } from '@mi-front/ui';
import dotProp from 'dot-prop-immutable';
import { create } from 'zustand';
import { DisplayableChunk, DisplayableJointFragment } from '../../../../../fragments/types';
import { chunkHelpers } from '../../../../../joint-fragments/helpers/chunk-helpers';
import { useSelectionStore } from '../../stores/selection.store';

/**
 * Input type for adding a chunk to the editor.
 * Extends FragmentChunk with the segments needed for visualization.
 */
export type ChunkInput = {
  label?: string;
  duration: Milliseconds;
  from: string;
  to: string;
  segments: readonly Segment[];
  highlights: string[];
};

/**
 * State for the joint fragments editor.
 * Manages the creation and editing of fragments with multiple audio chunks.
 */
interface FragmentEditorState {
  // State
  readonly isExpanded: boolean;
  readonly fragment: DisplayableJointFragment | null;
  readonly chunksPalette: readonly string[];

  // Actions

  /**
   * Initializes the editor with an empty fragment for a station.
   */
  initializeEditor: (stationId: string) => void;

  /**
   * Sets the current draft fragment and optionally the color palette.
   */
  setDraft: (fragment: DisplayableJointFragment | null, palette?: readonly string[]) => void;

  /**
   * Toggles the expanded/collapsed state of the editor.
   */
  toggleExpanded: () => void;

  /**
   * Adds a chunk to the fragment, assigning it a color from the palette.
   * Clears the selection after adding.
   */
  addChunk: (chunk: ChunkInput, stationId: string) => void;

  /**
   * Deletes a chunk from the fragment and returns its color to the palette.
   */
  deleteChunk: (chunk: DisplayableChunk) => void;

  /**
   * Moves a chunk left or right in the list.
   */
  moveChunk: (chunk: DisplayableChunk, direction: 'left' | 'right') => void;

  /**
   * Sets the label of a chunk.
   */
  setChunkLabel: (chunk: DisplayableChunk, label: string) => void;

  /**
   * Closes the editor and clears the state.
   */
  closeEditor: () => void;

  // Getters

  /**
   * Checks if a chunk can be added based on the current selection.
   */
  canAddChunk: (stationId: string) => boolean;
}

const initialState = {
  isExpanded: true,
  fragment: { chunks: [] },
  chunksPalette: graphPalette.slice(),
};

export const useFragmentEditorStore = create<FragmentEditorState>()((set, get) => ({
  ...initialState,

  initializeEditor: (stationId: string) => {
    set({
      ...initialState,
      fragment: { chunks: [] },
      chunksPalette: graphPalette.slice(),
    });
  },

  setDraft: (fragment: DisplayableJointFragment | null, palette?: readonly string[]) => {
    set((state) => ({
      fragment,
      chunksPalette: palette ?? state.chunksPalette,
    }));
  },

  toggleExpanded: () => {
    set((state) => ({
      isExpanded: !state.isExpanded,
    }));
  },

  addChunk: (chunk: ChunkInput, stationId: string) => {
    const { fragment, chunksPalette } = get();

    if (!fragment) {
      throw new Error('Fragment is null');
    }

    if (!chunk.duration) {
      return;
    }

    // Color palette rotation
    let newPalette = chunksPalette.slice();
    const color = newPalette.shift();

    if (!newPalette.length) {
      newPalette = graphPalette.slice();
    }

    // Clear selection after adding
    useSelectionStore.getState().clearSelection(stationId);

    const displayableChunk: DisplayableChunk = {
      ...chunk,
      label: chunk.label ?? null,
      color: color!,
    };

    set({
      fragment: {
        ...fragment,
        chunks: chunkHelpers.addChunk<DisplayableChunk>(fragment.chunks, displayableChunk),
      },
      chunksPalette: newPalette,
    });
  },

  deleteChunk: (chunk: DisplayableChunk) => {
    const { fragment, chunksPalette } = get();

    if (!fragment) {
      throw new Error('Fragment is null');
    }

    set({
      fragment: {
        ...fragment,
        chunks: chunkHelpers.removeChunk<DisplayableChunk>(fragment.chunks, chunk),
      },
      // Return the color to the palette
      chunksPalette: chunksPalette.concat(chunk.color),
    });
  },

  moveChunk: (chunk: DisplayableChunk, direction: 'left' | 'right') => {
    const { fragment } = get();

    if (!fragment) {
      throw new Error('Fragment is null');
    }

    set({
      fragment: {
        ...fragment,
        chunks:
          direction === 'left'
            ? chunkHelpers.moveChunkLeft<DisplayableChunk>(fragment.chunks, chunk)
            : chunkHelpers.moveChunkRight<DisplayableChunk>(fragment.chunks, chunk),
      },
    });
  },

  setChunkLabel: (chunk: DisplayableChunk, label: string) => {
    const { fragment } = get();

    if (!fragment) {
      throw new Error('Fragment is null');
    }

    const chunkIndex = fragment.chunks.indexOf(chunk);

    set({
      fragment: {
        ...fragment,
        chunks: dotProp.set(fragment.chunks, `${chunkIndex}.label`, label),
      },
    });
  },

  closeEditor: () => {
    set({
      ...initialState,
      chunksPalette: graphPalette.slice(),
    });
  },

  canAddChunk: (stationId: string) => {
    const { fragment } = get();

    if (!useSelectionStore.getState().selectionExists(stationId) || !fragment) {
      return false;
    }

    const selection = useSelectionStore.getState().getSelectionRange(stationId);

    // Verify it doesn't overlap with existing chunks
    return !fragment.chunks.find((c) => {
      const chunkFrom = toTimestamp(c.from);
      const chunkTo = toTimestamp(c.to);
      return selection.from <= chunkTo && selection.to >= chunkFrom;
    });
  },
}));

/**
 * Type for creating a chunk from a time selection.
 * Used by the useAddChunkFromSelection hook.
 */
export interface ChunkFromSelectionParams {
  stationId: string;
  selection: { from: number; to: number };
  segments: readonly Segment[];
}

/**
 * Creates chunk data from a selection.
 */
export function createChunkFromSelection(params: ChunkFromSelectionParams): ChunkInput {
  return {
    label: undefined,
    duration: (params.selection.to - params.selection.from) as Milliseconds,
    from: toDateTimeStr(params.selection.from),
    to: toDateTimeStr(params.selection.to),
    segments: params.segments,
    highlights: [],
  };
}
